import { LanguageCode } from '@/interfaces/user.interface';
import { usePreferredLanguages } from '@vueuse/core';
import axios from 'axios';

/**
 * Resolve the API base URL at runtime.
 * In production, each business unit has its own domain (app.flokee.ai, app.cleepo.ai)
 * with a CloudFront behavior that proxies /api/* to api.engeni.com/*.
 * Using the same-origin path avoids CORS preflight (OPTIONS) requests.
 * Falls back to the VITE_APP_ENGENI_API_URL env var for local/staging or
 * when window is unavailable.
 */
function getApiBaseUrl(): string {
  if (import.meta.env.VITE_APP_ENVIRONMENT === 'production') {
    return `${window.location.origin}/api`;
  }

  return import.meta.env.VITE_APP_ENGENI_API_URL;
}

export const engeniApi = axios.create({
  baseURL: getApiBaseUrl(),
});

// Add token to request
engeniApi.interceptors.request.use((request) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    request.headers.Authorization = `Bearer ${token}`;
  }
  return request;
});

// add locale query param to request only if it's not logged in
engeniApi.interceptors.request.use((request) => {
  const token = localStorage.getItem('access_token');

  if (!token) {
    // get preferred language from browser
    const preferredLanguages = usePreferredLanguages();

    const preferredLocale = Object.values(LanguageCode).find((lang) =>
      preferredLanguages.value.includes(lang),
    );
    request.params = {
      ...request.params,
      locale: preferredLocale || LanguageCode.ES,
    };
  }

  return request;
});

engeniApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      if (error.response.status === 401) {
        const has_token = !!localStorage.getItem('access_token');

        // If there is no access token, we are on a public/unauthenticated flow
        // (e.g. auth register/login) and should NOT reload the page.
        if (!has_token) {
          return Promise.reject(error);
        }

        localStorage.clear();
        window.location.reload();
      }
    } else {
      // Handle the case when error.response is undefined
      console.error('Error response is undefined:', error);
    }
    return Promise.reject(error);
  },
);
